/* import __COLOCATED_TEMPLATE__ from './call-quality-detailed-metrics.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { later } from '@ember/runloop';
import type IntlService from 'embercom/services/intl';
import type CallQualityMonitor from 'embercom/utils/call-quality-monitor';
import type { QualityLevel } from 'embercom/utils/call-quality-monitor';

type Tab = 'overview' | 'audio' | 'network';

interface Signature {
  Args: {
    monitor: CallQualityMonitor;
    onClose: () => void;
    onViewSummary: () => void;
  };
}

interface TabItem {
  id: Tab;
  label: string;
}

export default class CallQualityDetailedMetrics extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @tracked activeTab: Tab = 'overview';
  @tracked copied = false;

  get tabs(): TabItem[] {
    return [
      { id: 'overview', label: this.intl.t('calling.modal.quality.tab-overview') },
      { id: 'audio', label: this.intl.t('calling.modal.quality.tab-audio') },
      { id: 'network', label: this.intl.t('calling.modal.quality.tab-network') },
    ];
  }

  get metrics() {
    return this.args.monitor.currentMetrics;
  }

  get networkLabel(): string {
    return this.qualityLabel(this.args.monitor.metricHealth.network);
  }

  get microphoneLabel(): string {
    return this.qualityLabel(this.args.monitor.metricHealth.microphone);
  }

  get speakerLabel(): string {
    return this.qualityLabel(this.args.monitor.metricHealth.speaker);
  }

  get rttQuality(): QualityLevel {
    let rtt = this.metrics.rtt;
    if (rtt > 400) {
      return 'poor';
    }
    if (rtt > 200) {
      return 'fair';
    }
    return 'good';
  }

  get jitterQuality(): QualityLevel {
    let jitter = this.metrics.jitter;
    if (jitter > 80) {
      return 'poor';
    }
    if (jitter > 30) {
      return 'fair';
    }
    return 'good';
  }

  get packetLossQuality(): QualityLevel {
    let loss = this.metrics.packetLossPercent;
    if (loss > 5) {
      return 'poor';
    }
    if (loss > 1) {
      return 'fair';
    }
    return 'good';
  }

  get formattedMetrics(): string {
    let metrics = this.metrics;

    return [
      `${this.intl.t('calling.modal.quality.connection-quality')}: ${this.networkLabel}`,
      `${this.intl.t('calling.modal.quality.microphone')}: ${this.microphoneLabel}`,
      `${this.intl.t('calling.modal.quality.speaker')}: ${this.speakerLabel}`,
      '',
      `${this.intl.t('calling.modal.quality.latency-rtt')}: ${metrics.rtt} ms`,
      `${this.intl.t('calling.modal.quality.jitter')}: ${metrics.jitter} ms`,
      `${this.intl.t('calling.modal.quality.packet-loss')}: ${metrics.packetLossPercent}%`,
      `${this.intl.t('calling.modal.quality.incoming-bitrate')}: ${metrics.bitrateReceive} kbps`,
      `${this.intl.t('calling.modal.quality.outgoing-bitrate')}: ${metrics.bitrateSend} kbps`,
      `${this.intl.t('calling.modal.quality.mos-score')}: ${metrics.mos}`,
      '',
      `${this.intl.t('calling.modal.quality.input-level')}: ${metrics.audioInputLevel}`,
      `${this.intl.t('calling.modal.quality.output-level')}: ${metrics.audioOutputLevel}`,
      `${this.intl.t('calling.modal.quality.codec')}: ${metrics.codec}`,
    ].join('\n');
  }

  @action switchTab(tab: Tab) {
    this.activeTab = tab;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'switched_tab',
      object: 'call_quality_detailed_metrics',
      place: 'call_modal',
      tab,
    });
  }

  @action async copyToClipboard() {
    try {
      await navigator.clipboard.writeText(this.formattedMetrics);
    } catch {
      return;
    }
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.copied = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'copied',
      object: 'call_quality_metrics',
      place: 'call_modal',
      tab: this.activeTab,
    });
    later(
      this,
      () => {
        if (!this.isDestroying && !this.isDestroyed) {
          this.copied = false;
        }
      },
      2000,
    );
  }

  private qualityLabel(level: QualityLevel): string {
    switch (level) {
      case 'good':
        return this.intl.t('calling.modal.quality.good');
      case 'fair':
        return this.intl.t('calling.modal.quality.fair');
      case 'muted':
        return this.intl.t('calling.modal.quality.muted');
      case 'no_input':
        return this.intl.t('calling.modal.quality.no-input');
      case 'no_audio':
        return this.intl.t('calling.modal.quality.no-audio');
      default:
        return this.intl.t('calling.modal.quality.poor');
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Calling::CallQualityDetailedMetrics': typeof CallQualityDetailedMetrics;
    'inbox2/calling/call-quality-detailed-metrics': typeof CallQualityDetailedMetrics;
  }
}
